import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Stack } from "../../components/ui/Stack";
import { Heading } from "../../components/ui/Heading";
import { Card } from "../../components/ui/Card";
import { Button } from "../../components/ui/Button";
import { Input } from "../../components/ui/Input";
import { Spinner } from "../../components/ui/Spinner";
import { Alert } from "../../components/ui/Alert";
import { useApi } from "../../context/ApiContext";
import { useCart } from "../../context/CartContext";
import { useCurrency } from "../../context/CurrencyContext";
import { useAuth } from "../../context/AuthContext";
import { useStore } from "../../context/StoreContext";
import {
  FaArrowLeft,
  FaArrowRight,
  FaPlus,
  FaMinus,
  FaTrash,
  FaTimes,
} from "react-icons/fa";
import { Price } from "../../components/ui/Price";
import { Image } from "../../components/ui/Image";
import { QuantitySelector } from "../../components/ui/QuantitySelector";
import { getResizedImageUrl, ImagePresets } from "../../utils/imageUtils";
import { findProductImage } from "../../utils/productImageUtils";
import { AddressForm, Address } from "../../components/AddressForm";
import { COUNTRIES } from "../../utils/countryCurrencyData";
import { VariantSelectionModal } from "../../components/ui/VariantSelectionModal";
interface Product {
  _id: string;
  title: string;
  price: number;
  basePrice?: number;
  compareAtPrice?: number;
  description?: string;
  images?: Array<{ _id: string; url: string; isPrimary?: boolean }>;
  variants?: Array<{
    _id: string;
    name: string;
    price: number;
    options?: Array<{ name: string; value: string }>;
  }>;
  variantOptions?: Array<{
    name: string;
    values: Array<{
      value: string;
      addOnPrice: number;
      inventoryQuantity?: number;
      isActive: boolean;
      image?: string;
      locationPricing?: Array<{
        locationId: string;
        price?: number;
        inventoryQuantity?: number;
        isAvailable?: boolean;
      }>;
    }>;
    required: boolean;
  }>;
  isDigital?: boolean;
}

interface OrderFormData {
  customerName: string;
  customerEmail: string;
  shippingAddress: Address | null;
  billingAddress: Address | null;
  orderDate: string;
  paymentMethod: string;
  orderNotes?: string;
}

type WizardStep = "items" | "address" | "review";

export const CreateOrder: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { api } = useApi();
  const { cart, addToCart, updateItem, removeItem, clearCart } = useCart();
  const { currency, location } = useCurrency();
  const { user } = useAuth();
  const { store, isPluginEnabled } = useStore();

  const [currentStep, setCurrentStep] = useState<WizardStep>("items");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [products, setProducts] = useState<Product[]>([]);
  const [addresses, setAddresses] = useState<Address[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [selectedVariant, setSelectedVariant] = useState<string>("");
  const [quantity, setQuantity] = useState(1);
  const [addressSearch, setAddressSearch] = useState("");
  const [useSameAddress, setUseSameAddress] = useState(true);
  const [showNewAddressForm, setShowNewAddressForm] = useState(false);
  const [showVariantModal, setShowVariantModal] = useState(false);

  const isDiscountsPluginEnabled = isPluginEnabled("discounts-promos");
  const [orderDiscountType, setOrderDiscountType] = useState<
    "" | "amount" | "percentage"
  >("");
  const [orderDiscountValue, setOrderDiscountValue] = useState<string>("");

  // Address form state
  const [addressFormData, setAddressFormData] = useState<Partial<Address>>({
    firstName: "",
    lastName: "",
    address1: "",
    address2: "",
    city: "",
    postalCode: "",
    country: "",
    phone: "",
    isDefault: false,
    type: "both",
  });

  // Infinite scroll and search state
  const [productsPage, setProductsPage] = useState(1);
  const [hasMoreProducts, setHasMoreProducts] = useState(true);
  const [productsLoading, setProductsLoading] = useState(false);
  const [searchTimeout, setSearchTimeout] = useState<NodeJS.Timeout | null>(
    null,
  );

  const [formData, setFormData] = useState<OrderFormData>({
    customerName: "",
    customerEmail: user?.email || "",
    shippingAddress: null,
    billingAddress: null,
    orderDate: new Date().toISOString().split("T")[0],
    paymentMethod: "",
    orderNotes: "",
  });

  const [shippingRates, setShippingRates] = useState<
    Array<{
      id: string;
      name: string;
      price: number;
      isDefault?: boolean;
      carrierProvider?: string;
      estimatedDays: { min: number; max: number };
    }>
  >([]);
  const [shippingRatesLoading, setShippingRatesLoading] = useState(false);
  const [selectedShippingMethodId, setSelectedShippingMethodId] =
    useState<string>("");

  const isDigitalOnlyOrder = useMemo(() => {
    if (!cart?.items?.length) return false;
    return cart.items.every((item) => {
      const p = item.product as { isDigital?: boolean };
      return p?.isDigital === true;
    });
  }, [cart]);

  const needsShippingMethod =
    !isDigitalOnlyOrder && formData.paymentMethod !== "cash_on_pickup";

  const shippingCountryCode = useMemo(() => {
    const c = formData.shippingAddress?.country;
    if (!c) return undefined;
    const found = COUNTRIES.find(
      (country) =>
        country.name.toLowerCase() === c.toLowerCase() ||
        country.code.toLowerCase() === c.toLowerCase(),
    );
    return found?.code;
  }, [formData.shippingAddress?.country]);

  const fetchShippingRates = useCallback(async () => {
    if (!cart?._id || !needsShippingMethod) {
      setShippingRates([]);
      setSelectedShippingMethodId("");
      return;
    }
    setShippingRatesLoading(true);
    try {
      const response = await api.post("/shipping/rates", {
        cartId: cart._id,
        cartTotal: cart.subtotal || 0,
        ...(shippingCountryCode && { countryCode: shippingCountryCode }),
      });
      const rates = response.data.rates || [];
      setShippingRates(rates);
      setSelectedShippingMethodId((prev) => {
        if (rates.some((r: { id: string }) => r.id === prev)) return prev;
        const defaultRate = rates.find((r: { isDefault?: boolean }) => r.isDefault);
        return defaultRate?.id || rates[0]?.id || "";
      });
    } catch (err: any) {
      console.error("Error fetching shipping rates:", err);
      setShippingRates([]);
      setSelectedShippingMethodId("");
    } finally {
      setShippingRatesLoading(false);
    }
  }, [api, cart, needsShippingMethod, shippingCountryCode]);

  useEffect(() => {
    if (currentStep !== "address" || !cart?._id) return;
    if (!needsShippingMethod) {
      setShippingRates([]);
      setSelectedShippingMethodId("");
      return;
    }
    void fetchShippingRates();
  }, [
    currentStep,
    cart?._id,
    cart?.subtotal,
    needsShippingMethod,
    shippingCountryCode,
    fetchShippingRates,
  ]);

  const shippingForOrder = useMemo(() => {
    if (!needsShippingMethod) return 0;
    const rate = shippingRates.find((r) => r.id === selectedShippingMethodId);
    return typeof rate?.price === "number" ? rate.price : 0;
  }, [needsShippingMethod, shippingRates, selectedShippingMethodId]);

  // Helper function to calculate order-level discount amount
  const calculateOrderDiscount = (): number => {
    if (
      !cart ||
      !isDiscountsPluginEnabled ||
      !orderDiscountType ||
      !orderDiscountValue
    ) {
      return 0;
    }

    const parsedDiscountValue = parseFloat(orderDiscountValue);
    if (isNaN(parsedDiscountValue) || parsedDiscountValue <= 0) {
      return 0;
    }

    const baseAmount = cart.subtotal + cart.tax + shippingForOrder;
    let discountAmount = 0;
    if (orderDiscountType === "percentage") {
      discountAmount = baseAmount * (parsedDiscountValue / 100);
    } else {
      discountAmount = parsedDiscountValue;
    }
    // Cap discount at base amount and round to 2 decimals
    discountAmount = Math.max(0, Math.min(baseAmount, discountAmount));
    return Math.round(discountAmount * 100) / 100;
  };

  // Update email when user changes
  useEffect(() => {
    if (user?.email) {
      setFormData((prev) => ({
        ...prev,
        customerEmail: user.email,
      }));
    }
  }, [user]);

  // Fetch products with infinite scroll and search
  const fetchProducts = async (
    page: number = 1,
    search: string = "",
    append: boolean = false,
  ) => {
    if (productsLoading) return;

    setProductsLoading(true);
    try {
      const searchQuery = search ? `&search=${encodeURIComponent(search)}` : "";
      const response = await api.get(
        `/products?limit=20&page=${page}&isActive=true&populate=variants${searchQuery}`,
      );

      const newProducts = response.data.products || [];
      const totalProducts = response.data.total || 0;

      if (append) {
        setProducts((prev) => [...prev, ...newProducts]);
      } else {
        setProducts(newProducts);
      }

      // Check if there are more products to load
      const hasMore =
        newProducts.length === 20 &&
        products.length + newProducts.length < totalProducts;
      setHasMoreProducts(hasMore);
    } catch (err: any) {
      console.error("Error fetching products:", err);
    } finally {
      setProductsLoading(false);
    }
  };

  // Initial load and search effect
  useEffect(() => {
    // Clear previous timeout
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }

    // Set new timeout for search
    const timeout = setTimeout(() => {
      setProductsPage(1);
      setHasMoreProducts(true);
      fetchProducts(1, searchTerm, false);
    }, 300); // 300ms debounce

    setSearchTimeout(timeout);

    return () => {
      if (timeout) clearTimeout(timeout);
    };
  }, [searchTerm, api]);

  // Load more products on scroll
  const handleLoadMore = () => {
    if (hasMoreProducts && !productsLoading) {
      const nextPage = productsPage + 1;
      setProductsPage(nextPage);
      fetchProducts(nextPage, searchTerm, true);
    }
  };

  // Fetch addresses
  useEffect(() => {
    const fetchAddresses = async () => {
      try {
        const response = await api.get("/addresses");
        const addresses = response.data || []; // Use response.data directly like Addresses.tsx
        setAddresses(addresses);
      } catch (err: any) {
        console.error("Error fetching addresses:", err);
      }
    };
    fetchAddresses();
  }, [api]);

  const filteredAddresses = addresses.filter((address) =>
    `${address.firstName} ${address.lastName} ${address.address1} ${address.city}`
      .toLowerCase()
      .includes(addressSearch.toLowerCase()),
  );

  const handleAddToCart = async () => {
    if (!selectedProduct) return;

    // Check if product has variant options
    const hasVariantOptions =
      selectedProduct.variantOptions &&
      selectedProduct.variantOptions.length > 0;

    if (hasVariantOptions) {
      setShowVariantModal(true);
      return;
    }

    try {
      const variantOptions = selectedVariant
        ? selectedProduct.variants
            ?.find((v) => v._id === selectedVariant)
            ?.options?.map((opt) => ({
              optionName: opt.name,
              optionValue: opt.value,
            })) || []
        : [];

      await addToCart(
        selectedProduct._id,
        quantity,
        selectedVariant || undefined,
        variantOptions,
      );

      // Reset selection
      setSelectedProduct(null);
      setSelectedVariant("");
      setQuantity(1);
    } catch (err: any) {
      console.error("Error adding to cart:", err);
      setError("Failed to add to cart");
    }
  };

  const handleAddToCartFromModal = async (
    productId: string,
    quantity: number,
    selectedVariantOptions?: Array<{ optionName: string; optionValue: string }>,
  ) => {
    if (!selectedProduct) return;

    try {
      await addToCart(productId, quantity, undefined, selectedVariantOptions);

      // Reset selection and close modal
      setSelectedProduct(null);
      setSelectedVariant("");
      setQuantity(1);
      setShowVariantModal(false);
    } catch (err: any) {
      console.error("Error adding to cart:", err);
      setError("Failed to add to cart");
    }
  };

  const handleAddressSelect = (
    address: Address,
    type: "shipping" | "billing",
  ) => {
    setFormData((prev) => ({
      ...prev,
      [`${type}Address`]: address,
    }));
  };

  const handleCreateNewAddress = async () => {
    const required = [
      "firstName",
      "lastName",
      "address1",
      "city",
      "postalCode",
      "country",
    ];
    const newErrors: Record<string, string> = {};
    required.forEach((field) => {
      const value = addressFormData[field as keyof typeof addressFormData];
      if (!value || (typeof value === "string" && !value.trim())) {
        newErrors[field] =
          `${field.charAt(0).toUpperCase() + field.slice(1)} is required`;
      }
    });

    if (Object.keys(newErrors).length > 0) {
      setError("Please fill in all required fields");
      return;
    }

    try {
      setLoading(true);
      const response = await api.post("/addresses", addressFormData);
      const createdAddress = response.data;

      // Add to addresses list
      setAddresses((prev) => [...prev, createdAddress]);

      // Auto-select as shipping address
      handleAddressSelect(createdAddress, "shipping");

      // Reset form
      setAddressFormData({
        firstName: "",
        lastName: "",
        address1: "",
        address2: "",
        city: "",
        postalCode: "",
        country: "",
        phone: "",
        isDefault: false,
      });
      setShowNewAddressForm(false);
    } catch (err: any) {
      console.error("Error creating address:", err);
      setError("Failed to create address");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateOrder = async () => {
    if (!cart || cart.items.length === 0) {
      setError("Cart is empty");
      return;
    }
    if (!formData.customerEmail) {
      setError("Customer email is required");
      return;
    }

    if (!formData.shippingAddress) {
      setError("Shipping address is required");
      return;
    }

    if (!formData.paymentMethod) {
      setError("Payment method is required");
      return;
    }

    if (needsShippingMethod) {
      if (!selectedShippingMethodId) {
        setError(
          t("checkout.shippingMethodRequired") ||
            "Please select a shipping method",
        );
        return;
      }
      if (shippingRates.length === 0) {
        setError(
          t("checkout.noShippingMethodsConfigured") ||
            "No shipping methods are available",
        );
        return;
      }
    }

    try {
      setLoading(true);
      setError(null);

      // Calculate order-level discount if applicable
      const discountAmount = calculateOrderDiscount();
      const totalWithDiscount =
        Math.round(
          (cart.subtotal + cart.tax + shippingForOrder - discountAmount) * 100,
        ) / 100;

      const parsedDiscountValue = orderDiscountValue
        ? parseFloat(orderDiscountValue)
        : 0;
      const shouldApplyDiscount =
        discountAmount > 0 &&
        orderDiscountType &&
        !isNaN(parsedDiscountValue) &&
        parsedDiscountValue > 0;

      const orderData = {
        items: cart.items.map((item) => ({
          product: item.product._id,
          quantity: item.quantity,
          price: item.price,
          variant: item.variant?._id,
          selectedVariantOptions: item.selectedVariantOptions,
          customization: item.customization,
        })),
        customerName: useSameAddress && formData.shippingAddress
          ? `${formData.shippingAddress.firstName} ${formData.shippingAddress.lastName}`
          : formData.billingAddress
            ? `${formData.billingAddress.firstName} ${formData.billingAddress.lastName}`
            : "",
        customerEmail: formData.customerEmail,
        shippingAddress: formData.shippingAddress,
        billingAddress: useSameAddress
          ? formData.shippingAddress
          : formData.billingAddress,
        orderDate: formData.orderDate,
        paymentMethod: formData.paymentMethod,
        orderNotes: formData.orderNotes,
        subtotal: cart.subtotal,
        tax: cart.tax,
        total: totalWithDiscount,
        currency: currency,
        shipping: shippingForOrder,
        shippingMethodId: needsShippingMethod
          ? selectedShippingMethodId
          : undefined,
        discount: shouldApplyDiscount ? discountAmount : undefined,
        discountType: shouldApplyDiscount ? orderDiscountType : undefined,
        discountValue: shouldApplyDiscount ? parsedDiscountValue : undefined,
      };

      const response = await api.post("/orders/admin/create", orderData);

      const createdOrderId = response?.data?.order?._id;

      // Clear the cart after successful order creation
      await clearCart();

      // Navigate to order details page
      if (createdOrderId) {
        navigate(`/admin/orders/${createdOrderId}`);
      }
    } catch (err: any) {
      setError(err.response?.data?.error || "Failed to create order");
    } finally {
      setLoading(false);
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case "items":
        return (
          <Card padding="lg">
            <Stack spacing="lg">
              <Heading size="lg">{t("admin.createOrder.selectItems")}</Heading>

              {/* Product Search and Selection */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Product Selection */}
                <div>
                  <Input
                    placeholder={t("admin.createOrder.searchProducts")}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="mb-4"
                  />

                  <div
                    className="max-h-96 overflow-y-auto space-y-2 border rounded-lg p-4"
                    onScroll={(e) => {
                      const element = e.currentTarget;
                      if (
                        element.scrollHeight - element.scrollTop <=
                          element.clientHeight + 100 &&
                        hasMoreProducts &&
                        !productsLoading
                      ) {
                        handleLoadMore();
                      }
                    }}
                  >
                    {products.map((product: Product) => (
                      <div
                        key={product._id}
                        className={`p-3 border rounded cursor-pointer transition-colors ${
                          selectedProduct?._id === product._id
                            ? "border-blue-500 bg-blue-50"
                            : "border-gray-200 hover:border-gray-300"
                        }`}
                        onClick={() => {
                          setSelectedProduct(product);
                        }}
                      >
                        <div className="flex gap-3">
                          {/* Product Image */}
                          <div className="flex-shrink-0">
                            <Image
                              src={getResizedImageUrl(
                                findProductImage(product)?.url,
                                ImagePresets.cartItem,
                              )}
                              alt={product.title}
                              className="w-16 h-16 object-cover rounded"
                            />
                          </div>

                          {/* Product Info */}
                          <div className="flex-1">
                            <h4 className="font-medium">{product.title}</h4>
                            <p className="text-sm text-gray-600">
                              <Price
                                amount={product.price}
                                currency={currency}
                              />
                            </p>
                            {product.variantOptions &&
                              product.variantOptions.length > 0 && (
                                <p className="text-xs text-gray-500">
                                  {product.variantOptions.length} variant
                                  options available
                                </p>
                              )}
                          </div>
                        </div>
                      </div>
                    ))}

                    {/* Loading indicator for infinite scroll */}
                    {productsLoading && (
                      <div className="flex justify-center py-4">
                        <Spinner size="sm" />
                      </div>
                    )}

                    {/* End of products message */}
                    {!hasMoreProducts && products.length > 0 && (
                      <div className="text-center text-gray-500 text-sm py-2">
                        {t("admin.createOrder.noMoreProducts")}
                      </div>
                    )}
                  </div>
                </div>

                {/* Product Details and Add to Cart */}
                <div>
                  {selectedProduct && (
                    <Card padding="md">
                      <Stack spacing="md">
                        <h3 className="font-semibold">
                          {selectedProduct.title}
                        </h3>

                        <div>
                          <label className="block text-sm font-medium mb-2">
                            {t("admin.createOrder.quantity")}
                          </label>
                          <QuantitySelector
                            value={quantity}
                            onChange={setQuantity}
                            min={1}
                          />
                        </div>

                        <Button
                          onClick={handleAddToCart}
                          className="w-full"
                          disabled={!selectedProduct || loading}
                        >
                          {selectedProduct.variantOptions &&
                          selectedProduct.variantOptions.length > 0
                            ? t("admin.createOrder.selectOptions")
                            : t("admin.createOrder.addToCart")}
                        </Button>
                      </Stack>
                    </Card>
                  )}
                </div>
              </div>
            </Stack>
          </Card>
        );

      case "address":
        return (
          <Card padding="lg">
            <Stack spacing="lg">
              <Heading size="lg">
                {t("admin.createOrder.customerInformation")}
              </Heading>

              {/* Shipping Address */}
              <div>
                <Heading size="sm" className="mb-2">
                  {t("admin.createOrder.shippingAddress")}
                </Heading>

                <div className="flex gap-2 mb-2">
                  <Input
                    placeholder={t("admin.createOrder.searchAddresses")}
                    value={addressSearch}
                    onChange={(e) => setAddressSearch(e.target.value)}
                    className="flex-1"
                  />
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowNewAddressForm(!showNewAddressForm)}
                  >
                    {t("admin.createOrder.addNewAddress")}
                  </Button>
                </div>

                {/* New Address Form */}
                {showNewAddressForm && (
                  <Card padding="sm" className="mb-2">
                    <Heading size="sm" className="mb-2">
                      {t("admin.createOrder.newAddress")}
                    </Heading>
                    <AddressForm
                      showType
                      address={addressFormData as Address}
                      onChange={(address) => setAddressFormData(address)}
                    />
                    <div className="flex gap-2 mt-2">
                      <Button
                        size="sm"
                        onClick={handleCreateNewAddress}
                        disabled={loading}
                      >
                        {t("admin.createOrder.saveAddress")}
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setShowNewAddressForm(false)}
                      >
                        {t("common.cancel")}
                      </Button>
                    </div>
                  </Card>
                )}

                {filteredAddresses.length > 0 && (
                  <div className="max-h-48 overflow-y-auto space-y-1 border rounded-lg p-1.5">
                    {filteredAddresses.map((address) => (
                      <div
                        key={address._id}
                        className={`p-1.5 border rounded cursor-pointer transition-colors text-xs ${
                          formData.shippingAddress?._id === address._id
                            ? "border-blue-500 bg-blue-50"
                            : "border-gray-200 hover:border-gray-300"
                        }`}
                        onClick={() => handleAddressSelect(address, "shipping")}
                      >
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-medium">
                            {address.firstName} {address.lastName}
                          </span>
                          <span className="text-gray-500">•</span>
                          <span className="text-gray-600">{address.address1}</span>
                          {address.address2 && (
                            <>
                              <span className="text-gray-500">•</span>
                              <span className="text-gray-600">{address.address2}</span>
                            </>
                          )}
                          <span className="text-gray-500">•</span>
                          <span className="text-gray-600">{address.city}</span>
                          <span className="text-gray-500">•</span>
                          <span className="text-gray-600">{address.postalCode}</span>
                          <span className="text-gray-500">•</span>
                          <span className="text-gray-600">{address.country}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {needsShippingMethod && (
                <div>
                  <Heading size="sm" className="mb-2">
                    {t("checkout.shippingMethod")}
                  </Heading>
                  {shippingRatesLoading ? (
                    <div className="flex items-center gap-2 text-xs text-gray-600">
                      <Spinner size="sm" />
                      {t("common.loading")}
                    </div>
                  ) : shippingRates.length === 0 ? (
                    <div className="text-xs text-amber-800 bg-amber-50 border border-amber-100 rounded p-2">
                      {formData.shippingAddress?.country
                        ? t("checkout.shippingMethodsNotConfiguredForCountry", {
                            country: formData.shippingAddress.country,
                          })
                        : t("checkout.noShippingMethodsConfigured")}
                    </div>
                  ) : (
                    <div className="space-y-1.5 border rounded-lg p-1.5">
                      {shippingRates.map((rate) => (
                        <label
                          key={rate.id}
                          className={`flex items-start gap-2 cursor-pointer text-xs p-1.5 border rounded transition-colors ${
                            selectedShippingMethodId === rate.id
                              ? "border-blue-500 bg-blue-50"
                              : "border-gray-200 hover:border-gray-300"
                          }`}
                        >
                          <input
                            type="radio"
                            name="adminShippingMethod"
                            value={rate.id}
                            checked={selectedShippingMethodId === rate.id}
                            onChange={() => setSelectedShippingMethodId(rate.id)}
                            className="mt-0.5 text-blue-600"
                          />
                          <span className="flex-1 min-w-0">
                            <span className="flex items-center gap-2 flex-wrap font-medium">
                              {rate.name}
                              {rate.isDefault && (
                                <span className="text-[10px] bg-blue-100 text-blue-800 px-1.5 py-0.5 rounded">
                                  {t("admin.shipping.isDefault") || "Default"}
                                </span>
                              )}
                              {rate.carrierProvider && (
                                <span className="text-[10px] text-gray-500">
                                  ({rate.carrierProvider.toUpperCase()})
                                </span>
                              )}
                            </span>
                            <span className="text-gray-600 mt-0.5 block">
                              {rate.estimatedDays?.min ?? 0}-
                              {rate.estimatedDays?.max ?? 0}{" "}
                              {t("checkout.days")} · {t("checkout.price")}:{" "}
                              <Price
                                amount={rate.price}
                                currency={currency}
                                size="sm"
                                className="inline"
                              />
                            </span>
                          </span>
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Billing Address */}
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <input
                    type="checkbox"
                    id="useSameAddress"
                    checked={useSameAddress}
                    onChange={(e) => setUseSameAddress(e.target.checked)}
                  />
                  <label
                    htmlFor="useSameAddress"
                    className="text-sm font-medium"
                  >
                    {t("admin.createOrder.useSameAddress")}
                  </label>
                </div>

                {!useSameAddress && (
                  <div>
                    <Heading size="sm" className="mb-2">
                      {t("admin.createOrder.billingAddress")}
                    </Heading>

                    {filteredAddresses.length > 0 && (
                      <div className="max-h-48 overflow-y-auto space-y-1 border rounded-lg p-1.5">
                        {filteredAddresses.map((address) => (
                          <div
                            key={address._id}
                            className={`p-1.5 border rounded cursor-pointer transition-colors text-xs ${
                              formData.billingAddress?._id === address._id
                                ? "border-blue-500 bg-blue-50"
                                : "border-gray-200 hover:border-gray-300"
                            }`}
                            onClick={() =>
                              handleAddressSelect(address, "billing")
                            }
                          >
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="font-medium">
                                {address.firstName} {address.lastName}
                              </span>
                              <span className="text-gray-500">•</span>
                              <span className="text-gray-600">{address.address1}</span>
                              {address.address2 && (
                                <>
                                  <span className="text-gray-500">•</span>
                                  <span className="text-gray-600">{address.address2}</span>
                                </>
                              )}
                              <span className="text-gray-500">•</span>
                              <span className="text-gray-600">{address.city}</span>
                              <span className="text-gray-500">•</span>
                              <span className="text-gray-600">{address.postalCode}</span>
                              <span className="text-gray-500">•</span>
                              <span className="text-gray-600">{address.country}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Payment Method and Discount - Side by Side */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Payment Method Selection */}
                  <div>
                    <h3 className="font-semibold mb-3">
                      {t("admin.createOrder.paymentMethod")}
                    </h3>
                    <div className="space-y-2">
                      {(() => {
                        const countryCode = location?.countryCode;
                        let availableMethods = [];

                        if (
                          countryCode &&
                          store?.paymentMethodsByCountry?.[countryCode]
                        ) {
                          const countryMethods =
                            store.paymentMethodsByCountry[countryCode];
                          if (countryMethods.cashOnDelivery)
                            availableMethods.push({
                              id: "cash_on_delivery",
                              name: "Cash on Delivery",
                            });
                          if (countryMethods.cashOnPickup)
                            availableMethods.push({
                              id: "cash_on_pickup",
                              name: "Cash on Pickup",
                            });
                          if (countryMethods.payByInvoice)
                            availableMethods.push({
                              id: "pay_by_invoice",
                              name: "Pay by Invoice",
                            });
                        } else {
                          const legacyMethods = store?.paymentMethods;
                          if (legacyMethods) {
                            if (legacyMethods.cashOnDelivery)
                              availableMethods.push({
                                id: "cash_on_delivery",
                                name: "Cash on Delivery",
                              });
                            if (legacyMethods.cashOnPickup)
                              availableMethods.push({
                                id: "cash_on_pickup",
                                name: "Cash on Pickup",
                              });
                            if (legacyMethods.payByInvoice)
                              availableMethods.push({
                                id: "pay_by_invoice",
                                name: "Pay by Invoice",
                              });
                          }
                        }

                        return availableMethods.map((method) => (
                          <label
                            key={method.id}
                            className="flex items-center gap-2 cursor-pointer"
                          >
                            <input
                              type="radio"
                              name="paymentMethod"
                              value={method.id}
                              checked={formData.paymentMethod === method.id}
                              onChange={(e) =>
                                setFormData((prev) => ({
                                  ...prev,
                                  paymentMethod: e.target.value,
                                }))
                              }
                              className="text-blue-600"
                            />
                            <span className="text-sm">{method.name}</span>
                          </label>
                        ));
                      })()}
                    </div>
                  </div>

                  {/* Discount Section */}
                  {isDiscountsPluginEnabled && (
                    <div>
                      <h3 className="font-semibold mb-3">
                        {t("admin.orders.discount") || "Order Discount"}
                      </h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            {t("admin.productForm.discountType") ||
                              "Discount Type"}
                          </label>
                          <select
                            className="w-full border border-gray-300 rounded px-3 py-2"
                            value={orderDiscountType}
                            onChange={(e) =>
                              setOrderDiscountType(e.target.value as any)
                            }
                          >
                            <option value="">{t("admin.none") || "None"}</option>
                            <option value="amount">
                              {t("admin.productForm.discountAmount") || "Amount"}
                            </option>
                            <option value="percentage">
                              {t("admin.productForm.percentage") || "Percentage"}
                            </option>
                          </select>
                        </div>

                        <div>
                          <Input
                            label={
                              orderDiscountType === "percentage"
                                ? t("admin.productForm.percent") || "Percent"
                                : t("admin.productForm.discountAmount") ||
                                  "Amount"
                            }
                            type="number"
                            step="0.01"
                            min="0"
                            max={
                              orderDiscountType === "percentage"
                                ? "100"
                                : undefined
                            }
                            value={orderDiscountValue}
                            onChange={(e) =>
                              setOrderDiscountValue(e.target.value)
                            }
                            disabled={!orderDiscountType}
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </div>

              </div>

              {isDiscountsPluginEnabled && orderDiscountType && (
                <div>
                  <strong>
                    {t("admin.orders.discount") || "Order Discount"}:
                  </strong>{" "}
                  {orderDiscountType === "percentage"
                    ? `${orderDiscountValue || 0}%`
                    : `${orderDiscountValue || 0} ${currency}`}
                </div>
              )}
            </Stack>
          </Card>
        );

      case "review":
        return (
          <Card padding="lg">
            <Stack spacing="lg">
              <Heading size="lg">{t("admin.createOrder.reviewOrder")}</Heading>

              {/* Order Date */}
              <div>
                <Input
                  label={t("admin.createOrder.orderDate")}
                  type="date"
                  value={formData.orderDate}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      orderDate: e.target.value,
                    }))
                  }
                />
              </div>

              {/* Order Notes */}
              <div>
                <label className="block text-sm font-medium mb-2">
                  {t("admin.createOrder.orderNotes")}
                </label>
                <textarea
                  className="w-full p-3 border rounded-lg"
                  rows={3}
                  value={formData.orderNotes}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      orderNotes: e.target.value,
                    }))
                  }
                  placeholder={t("admin.createOrder.orderNotesPlaceholder")}
                />
              </div>
            </Stack>
          </Card>
        );

      default:
        return null;
    }
  };

  const canProceedToNext = () => {
    switch (currentStep) {
      case "items":
        return cart && cart.items.length > 0;
      case "address":
        if (!formData.paymentMethod) return false;  
        if (!formData.customerEmail || !formData.shippingAddress) return false;
        if (!needsShippingMethod) return true;
        if (shippingRatesLoading) return false;
        if (shippingRates.length === 0) return false;
        return !!selectedShippingMethodId;
      case "review":
        return (
          formData.customerEmail &&
          formData.shippingAddress &&
          formData.paymentMethod &&
          (!needsShippingMethod ||
            (!shippingRatesLoading &&
              shippingRates.length > 0 &&
              !!selectedShippingMethodId))
        );
      default:
        return false;
    }
  };

  const getNextStepLabel = () => {
    switch (currentStep) {
      case "items":
        return t("admin.createOrder.continueToAddress");
      case "address":
        return t("admin.createOrder.continueToReview");
      case "review":
        return t("admin.createOrder.createOrder");
      default:
        return t("common.next");
    }
  };

  const handleNext = () => {
    if (currentStep === "review") {
      handleCreateOrder();
    } else {
      const steps: WizardStep[] = ["items", "address", "review"];
      const currentIndex = steps.indexOf(currentStep);
      if (currentIndex < steps.length - 1) {
        setCurrentStep(steps[currentIndex + 1]);
      }
    }
  };

  const handlePrevious = () => {
    const steps: WizardStep[] = ["items", "address", "review"];
    const currentIndex = steps.indexOf(currentStep);
    if (currentIndex > 0) {
      setCurrentStep(steps[currentIndex - 1]);
    }
  };

  return (
    <div className="fixed inset-0 z-[9999] flex flex-col bg-white overflow-hidden">
      <div className="flex flex-col flex-1 min-h-0 w-full max-w-7xl mx-auto px-6 h-full">
        {/* Header - Progress Steps and Error */}
        <div className="flex-shrink-0 pb-4">
          {/* Progress Steps - Moved to top */}
          <div className="flex items-center justify-between py-3 border-b mb-3 relative">
            <div className="flex-1"></div>
            <div className="flex items-center space-x-4">
              {["items", "address", "review"].map((step, index) => (
                <React.Fragment key={step}>
                  <div className="flex items-center gap-2">
                    <div
                      className={`flex items-center justify-center w-8 h-8 rounded-full text-sm font-medium ${
                        currentStep === step
                          ? "bg-blue-600 text-white"
                          : ["items", "address", "review"].indexOf(currentStep) >
                              index
                            ? "bg-green-600 text-white"
                            : "bg-gray-200 text-gray-600"
                      }`}
                    >
                      {index + 1}
                    </div>
                    <span
                      className={`text-sm ${index === 0 ? "mr-4" : ""} ${
                        currentStep === step
                          ? "text-blue-600 font-medium"
                          : "text-gray-600"
                      }`}
                    >
                      {t(`admin.createOrder.steps.${step}`)}
                    </span>
                  </div>
                  {index < 2 && (
                    <div
                      className={`w-8 h-0.5 ml-2 ${
                        ["items", "address", "review"].indexOf(currentStep) >
                        index
                          ? "bg-green-600"
                          : "bg-gray-200"
                      }`}
                    />
                  )}
                </React.Fragment>
              ))}
            </div>
            <div className="flex-1 flex justify-end">
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate("/admin/orders")}
                className="flex items-center gap-2"
              >
                <FaTimes />
              </Button>
            </div>
          </div>

          {error && <Alert variant="error">{error}</Alert>}
        </div>

        {/* Main Content Area with Sidebar */}
        <div className="flex-1 flex gap-6 overflow-hidden min-h-0">
          {/* Step Content - Scrollable */}
          <div className="flex-1 overflow-y-auto py-4 min-h-0">
            {renderStepContent()}
          </div>

          {/* Order Summary Sidebar - Fixed */}
          <div className="w-80 flex-shrink-0 border-l pl-6">
            <div className="sticky top-0">
              <Card padding="sm">
                <Stack spacing="sm">
                  {/* Items Summary */}
                  {cart && cart.items.length > 0 && (
                    <div className="pb-1.5">
                      <div className="space-y-1.5 max-h-24 overflow-y-auto">
                        {cart.items.map((item) => {
                          const product = item.product as any;
                          const selectedVariantOptions:
                            | Array<{ optionName: string; optionValue: string }>
                            | undefined = item.selectedVariantOptions;
                          return (
                            <div
                              key={item._id}
                              className="text-xs border-b pb-1.5 last:border-0"
                            >
                              <div className="flex gap-2">
                                <div className="flex-shrink-0">
                                  <Image
                                    src={getResizedImageUrl(
                                      findProductImage(product)?.url,
                                      ImagePresets.cartItem,
                                    )}
                                    alt={product?.title || ""}
                                    className="w-12 h-12 object-cover rounded border"
                                  />
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="font-medium text-sm leading-tight">{product?.title}</div>
                                  {selectedVariantOptions &&
                                    selectedVariantOptions.length > 0 && (
                                      <div className="text-xs text-gray-600 mt-0.5 leading-tight">
                                        {selectedVariantOptions
                                          .map((opt) => `${opt.optionName}: ${opt.optionValue}`)
                                          .join(", ")}
                                      </div>
                                    )}
                                  <div className="flex justify-between items-center mt-0.5">
                                <div className="flex items-center gap-1">
                                  <button
                                    onClick={() =>
                                      updateItem(item._id, item.quantity - 1)
                                    }
                                    disabled={item.quantity <= 1}
                                    className="w-5 h-5 flex items-center justify-center border rounded text-xs disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
                                  >
                                    <FaMinus className="text-xs" />
                                  </button>
                                  <span className="text-xs text-gray-700 w-6 text-center">
                                    {item.quantity}
                                  </span>
                                  <button
                                    onClick={() =>
                                      updateItem(item._id, item.quantity + 1)
                                    }
                                    className="w-5 h-5 flex items-center justify-center border rounded text-xs hover:bg-gray-100"
                                  >
                                    <FaPlus className="text-xs" />
                                  </button>
                                  <button
                                    onClick={() => removeItem(item._id)}
                                    className="w-5 h-5 flex items-center justify-center border rounded text-xs text-red-600 hover:bg-red-50 ml-1"
                                  >
                                    <FaTrash className="text-xs" />
                                  </button>
                                </div>
                                    <Price
                                      amount={item.price * item.quantity}
                                      currency={currency}
                                      size="sm"
                                    />
                                  </div>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Pricing Summary */}
                  {cart && cart.items.length > 0 && (
                    <div className="space-y-1 pt-1.5 border-t">
                      <div className="flex justify-between text-xs">
                        <span className="text-gray-600">
                          {t("admin.createOrder.subtotal")}
                        </span>
                        <Price
                          amount={cart.subtotal}
                          currency={currency}
                          size="sm"
                        />
                      </div>
                      {cart.tax > 0 && (
                        <div className="flex justify-between text-xs">
                          <span className="text-gray-600">
                            {t("checkout.tax") || t("admin.createOrder.tax") || "Tax"}
                          </span>
                          <Price
                            amount={cart.tax}
                            currency={currency}
                            size="sm"
                          />
                        </div>
                      )}
                      <div className="flex justify-between text-xs">
                        <span className="text-gray-600">
                          {t("checkout.shipping") || t("admin.createOrder.shipping") || "Shipping"}
                        </span>
                        <Price
                          amount={shippingForOrder}
                          currency={currency}
                          size="sm"
                        />
                      </div>
                      {isDiscountsPluginEnabled &&
                        orderDiscountType &&
                        orderDiscountValue && (
                          <div className="flex justify-between text-xs text-green-600">
                            <span>
                              {t("admin.createOrder.orderDiscount")} (
                              {orderDiscountType === "percentage"
                                ? `${orderDiscountValue}%`
                                : orderDiscountType}
                              )
                            </span>
                            <span className="inline-flex items-center gap-0.5">
                              <span>-</span>
                              <Price
                                amount={calculateOrderDiscount()}
                                currency={currency}
                                size="sm"
                                className="text-green-600"
                              />
                            </span>
                          </div>
                        )}
                      <div className="flex justify-between pt-1.5 border-t font-semibold text-sm">
                        <span>{t("admin.createOrder.total")}</span>
                        <Price
                          amount={
                            Math.round(
                              (cart.subtotal +
                                cart.tax +
                                shippingForOrder -
                                calculateOrderDiscount()) *
                                100,
                            ) / 100
                          }
                          currency={currency}
                          size="sm"
                        />
                      </div>
                    </div>
                  )}

                  {/* Address Summary (for address and review steps) */}
                  {(currentStep === "address" || currentStep === "review") && (
                    <div className="pt-1.5 border-t">
                      <h4 className="font-semibold text-xs mb-1.5 text-gray-700">
                        {t("admin.createOrder.customerInformation")}
                      </h4>
                      {formData.customerEmail && (
                        <div className="text-xs mb-1.5">
                          <div className="text-gray-600 mb-0.5 text-xs">
                            {t("admin.createOrder.customerEmail")}
                          </div>
                          <div className="leading-tight">{formData.customerEmail}</div>
                        </div>
                      )}
                      {(useSameAddress && formData.shippingAddress) || formData.billingAddress ? (
                        <div className="text-xs mb-1.5">
                          <div className="text-gray-600 mb-0.5 text-xs">
                            {t("admin.createOrder.customerName")}
                          </div>
                          <div className="leading-tight">
                            {useSameAddress && formData.shippingAddress
                              ? `${formData.shippingAddress.firstName} ${formData.shippingAddress.lastName}`
                              : formData.billingAddress
                                ? `${formData.billingAddress.firstName} ${formData.billingAddress.lastName}`
                                : ""}
                          </div>
                        </div>
                      ) : null}
                      {(formData.shippingAddress || formData.billingAddress) && (
                        <div className="grid grid-cols-2 gap-2 text-xs">
                          {formData.shippingAddress && (
                            <div>
                              <div className="text-gray-600 mb-0.5 font-semibold text-xs">
                                {t("admin.createOrder.shippingAddress")}
                              </div>
                              <div className="text-gray-700 leading-tight">
                                {formData.shippingAddress.firstName}{" "}
                                {formData.shippingAddress.lastName}
                                <br />
                                {formData.shippingAddress.address1}
                                {formData.shippingAddress.address2 && (
                                  <>
                                    <br />
                                    {formData.shippingAddress.address2}
                                  </>
                                )}
                                <br />
                                {formData.shippingAddress.city},{" "}
                                {formData.shippingAddress.postalCode}
                                <br />
                                {formData.shippingAddress.country}
                              </div>
                            </div>
                          )}
                          <div>
                            <div className="text-gray-600 mb-0.5 font-semibold text-xs">
                              {t("admin.createOrder.billingAddress")}
                            </div>
                            {useSameAddress && formData.shippingAddress ? (
                              <div className="text-gray-700 leading-tight">
                                {formData.shippingAddress.firstName}{" "}
                                {formData.shippingAddress.lastName}
                                <br />
                                {formData.shippingAddress.address1}
                                {formData.shippingAddress.address2 && (
                                  <>
                                    <br />
                                    {formData.shippingAddress.address2}
                                  </>
                                )}
                                <br />
                                {formData.shippingAddress.city},{" "}
                                {formData.shippingAddress.postalCode}
                                <br />
                                {formData.shippingAddress.country}
                              </div>
                            ) : formData.billingAddress ? (
                              <div className="text-gray-700 leading-tight">
                                {formData.billingAddress.firstName}{" "}
                                {formData.billingAddress.lastName}
                                <br />
                                {formData.billingAddress.address1}
                                {formData.billingAddress.address2 && (
                                  <>
                                    <br />
                                    {formData.billingAddress.address2}
                                  </>
                                )}
                                <br />
                                {formData.billingAddress.city},{" "}
                                {formData.billingAddress.postalCode}
                                <br />
                                {formData.billingAddress.country}
                              </div>
                            ) : (
                              <div className="text-gray-500 text-xs">-</div>
                            )}
                          </div>
                        </div>
                      )}
                      {needsShippingMethod &&
                        selectedShippingMethodId &&
                        shippingRates.length > 0 && (
                          <div className="text-xs mb-1.5 pt-1.5 border-t">
                            <div className="text-gray-600 mb-0.5 text-xs">
                              {t("checkout.shippingMethod")}
                            </div>
                            <div className="leading-tight">
                              {shippingRates.find(
                                (r) => r.id === selectedShippingMethodId,
                              )?.name ?? selectedShippingMethodId}
                            </div>
                          </div>
                        )}
                      {formData.paymentMethod && (
                        <div className="text-xs mb-1.5 mt-1.5 pt-1.5 border-t">
                          <div className="text-gray-600 mb-0.5 text-xs">
                            {t("admin.createOrder.paymentMethod")}
                          </div>
                          <div className="leading-tight">{formData.paymentMethod}</div>
                        </div>
                      )}
                      <div className="text-xs mb-1.5 pt-1.5 border-t">
                        <div className="text-gray-600 mb-0.5 text-xs">
                          {t("admin.createOrder.orderDate")}
                        </div>
                        <div className="leading-tight">{formData.orderDate || "-"}</div>
                      </div>
                      {formData.orderNotes && (
                        <div className="text-xs pt-1.5 border-t">
                          <div className="text-gray-600 mb-0.5 text-xs">
                            {t("admin.createOrder.orderNotes")}
                          </div>
                          <div className="leading-tight whitespace-pre-wrap">{formData.orderNotes}</div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Empty State */}
                  {(!cart || cart.items.length === 0) && (
                    <div className="text-xs text-gray-500 text-center py-2">
                      {t("cart.empty") || "No items in cart"}
                    </div>
                  )}
                </Stack>
              </Card>
            </div>
          </div>
        </div>

        {/* Navigation Footer - Always Visible */}
        <div className="flex-shrink-0 border-t pt-2 pb-1">
          <div className="flex justify-between">
            <Button
              variant="outline"
              size="sm"
              onClick={handlePrevious}
              disabled={currentStep === "items"}
            >
              <FaArrowLeft className="mr-1.5 text-xs" />
              {t("common.previous")}
            </Button>

            <Button
              size="sm"
              onClick={handleNext}
              disabled={!canProceedToNext() || loading}
            >
              {loading && <Spinner size="sm" className="mr-1.5" />}
              {getNextStepLabel()}
              {currentStep !== "review" && <FaArrowRight className="ml-1.5 text-xs" />}
            </Button>
          </div>
        </div>
      </div>

      {/* Variant Selection Modal */}
      {selectedProduct && (
        <VariantSelectionModal
          isOpen={showVariantModal}
          onClose={() => setShowVariantModal(false)}
          product={selectedProduct}
          onAddToCart={handleAddToCartFromModal}
        />
      )}
    </div>
  );
};

export default CreateOrder;
